import { pure } from '@core/helpers/pure';
import { TUI_CONFIRM } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestService } from '@core/services/request/request.service';
import { RequestDevice } from '@core/types/RequestDevice';
import { TuiDialogService, TuiIcon, TuiButton } from '@taiga-ui/core';
import { filter, Observable, Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { WarehouseService } from '@core/services/warehouse/warehouse.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  RequestFormDevicesDeviceRemoverTranslations,
  RequestFormDevicesDeviceRemoverTranslationsPayload,
} from '@core/types';

@Component({
  selector: 'x-request-device-remover',
  imports: [TuiButton, TuiIcon, AsyncPipe],
  templateUrl: './request-device-remover.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestDeviceRemoverComponent implements OnInit {
  private readonly requestService = inject(RequestService);
  private readonly requestApiService = inject(RequestsApiService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService$ = inject(TuiDialogService);
  readonly permissionService = inject(OutkeepPermissionService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private translates: RequestFormDevicesDeviceRemoverTranslationsPayload = {
    [RequestFormDevicesDeviceRemoverTranslations.DialogRemoveDevice]:
      'Вы действительно хотите удалить устройство?',
    [RequestFormDevicesDeviceRemoverTranslations.DialogRemoveDeviceYes]:
      'Да, удалить',
    [RequestFormDevicesDeviceRemoverTranslations.DialogRemoveDeviceNo]:
      'Отмена',
  };
  readonly $request = this.requestService.$request;
  readonly $device = input.required<RequestDevice>({ alias: 'device' });
  readonly removeRequestDevice$ = new Subject<void>();

  requestDeviceRemoved = output<void>();

  @pure
  get device() {
    return this.$device();
  }

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormDevicesDeviceRemoverTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.removeRequestDevice$
      .pipe(
        switchMap(() => this.performRequestDeviceRemoval$()),
        switchMap(() =>
          this.warehouseService.unSellDevice$(
            this.$request(),
            this.device.name,
          ),
        ),
        tap(() => {
          this.requestDeviceRemoved.emit();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private performRequestDeviceRemoval$(): Observable<void> {
    return this.dialogService$
      .open<boolean>(TUI_CONFIRM, {
        label:
          this.translates[
            RequestFormDevicesDeviceRemoverTranslations.DialogRemoveDevice
          ],
        size: 's',
        closable: false,
        data: {
          yes: this.translates[
            RequestFormDevicesDeviceRemoverTranslations.DialogRemoveDeviceYes
          ],
          no: this.translates[
            RequestFormDevicesDeviceRemoverTranslations.DialogRemoveDeviceNo
          ],
        },
      })
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.requestApiService.removeRequestDevice$(this.device.id),
        ),
      );
  }
}
